import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Material, MaterialType } from '../types/material.types';
import { useMasterDataStore } from '@/features/master-data/store/master-data-store';
import { Loader2, Package, Wrench } from 'lucide-react';
import { ProductFields } from './product-fields';

interface MaterialFormData {
  code: string;
  externalCode: string;
  sapCode: string;
  name: string;
  description: string;
  tariffHeading: string;
  tariffRate: number;
  lobId: string;
  type: MaterialType;
  sku: string;
  brand: string;
  model: string;
  unitOfMeasure: string;
  weight: number;
  height: number;
  width: number;
  depth: number;
}

interface MaterialFormProps {
  material?: Material;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function MaterialForm({ material, onSubmit, onCancel, isLoading }: MaterialFormProps) {
  const { lobs, fetchLOBs } = useMasterDataStore();

  useEffect(() => {
    if (lobs.length === 0) {
      fetchLOBs();
    }
  }, []);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<MaterialFormData>({
    defaultValues: material
      ? {
          code: material.code,
          externalCode: material.externalCode || '',
          sapCode: (material as any).sapCode || '',
          name: material.name,
          description: material.description || '',
          tariffHeading: material.tariffHeading || '',
          tariffRate: material.activeTariffRate ?? 0,
          lobId: material.lobId || '',
          type: material.type,
          sku: material.sku || '',
          brand: material.brand || '',
          model: material.model || '',
          unitOfMeasure: material.unitOfMeasure || '',
          weight: material.weight ?? 0,
          height: material.height ?? 0,
          width: material.width ?? 0,
          depth: material.depth ?? 0,
        }
      : {
          code: '',
          externalCode: '',
          sapCode: '',
          name: '',
          description: '',
          tariffHeading: '',
          tariffRate: 0,
          lobId: '',
          type: 'PRODUCT',
          sku: '',
          brand: '',
          model: '',
          unitOfMeasure: '',
          weight: 0,
          height: 0,
          width: 0,
          depth: 0,
        },
  });

  const selectedLobId = watch('lobId');
  const selectedType = watch('type');

  const onFormSubmit = (data: MaterialFormData) => {
    return onSubmit({
      code: data.code,
      externalCode: data.externalCode || null,
      sapCode: data.sapCode || null,
      name: data.name,
      description: data.description || null,
      tariffHeading: data.tariffHeading || null,
      tariffRate: data.tariffRate || undefined,
      lobId: data.lobId || null,
      type: data.type,
      sku: data.type === 'PRODUCT' ? data.sku || null : null,
      brand: data.type === 'PRODUCT' ? data.brand || null : null,
      model: data.type === 'PRODUCT' ? data.model || null : null,
      unitOfMeasure: data.type === 'PRODUCT' ? data.unitOfMeasure || null : null,
      weight: data.type === 'PRODUCT' && data.weight ? data.weight : null,
      width: data.type === 'PRODUCT' && data.width ? data.width : null,
      height: data.type === 'PRODUCT' && data.height ? data.height : null,
      depth: data.type === 'PRODUCT' && data.depth ? data.depth : null,
    });
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="code">Código *</Label>
          <Input
            id="code"
            placeholder="MAT-001"
            {...register('code', { required: 'Código requerido' })}
            disabled={isLoading || !!material}
            className="uppercase"
          />
          {errors.code && <p className="text-xs text-destructive">{errors.code.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="externalCode">Código Externo</Label>
          <Input
            id="externalCode"
            placeholder="Código del proveedor"
            {...register('externalCode')}
            disabled={isLoading}
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="sapCode">Código SAP</Label>
        <Input
          id="sapCode"
          placeholder="Código equivalente en SAP (opcional)"
          maxLength={64}
          {...register('sapCode')}
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label>Tipo *</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={selectedType === 'PRODUCT' ? 'default' : 'outline'}
            className="flex-1"
            onClick={() => setValue('type', 'PRODUCT')}
            disabled={isLoading || !!material}
          >
            <Package className="w-4 h-4 mr-2" />
            Producto
          </Button>
          <Button
            type="button"
            variant={selectedType === 'SERVICE' ? 'default' : 'outline'}
            className="flex-1"
            onClick={() => setValue('type', 'SERVICE')}
            disabled={isLoading || !!material}
          >
            <Wrench className="w-4 h-4 mr-2" />
            Servicio
          </Button>
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="name">Nombre *</Label>
        <Input
          id="name"
          placeholder="Nombre del material"
          {...register('name', { required: 'Nombre requerido' })}
          disabled={isLoading}
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Descripción</Label>
        <Textarea
          id="description"
          rows={2}
          placeholder="Descripción del material"
          {...register('description')}
          disabled={isLoading}
        />
      </div>

      <div className="space-y-2">
        <Label>Línea de Negocio</Label>
        <Select
          value={selectedLobId || ''}
          onValueChange={(value) => setValue('lobId', value === '__none__' ? '' : value)}
          disabled={isLoading}
        >
          <SelectTrigger>
            <SelectValue placeholder="Seleccionar línea de negocio" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__none__">Sin asignar</SelectItem>
            {lobs
              .filter((l) => l.isActive)
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((lob) => (
                <SelectItem key={lob.id} value={lob.id}>
                  {lob.code} — {lob.name}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="tariffHeading">Partida Arancelaria</Label>
          <Input
            id="tariffHeading"
            placeholder="8471.50"
            {...register('tariffHeading')}
            disabled={isLoading}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="tariffRate">Gravamen Arancelario %</Label>
          <Input
            id="tariffRate"
            type="number"
            step="0.01"
            min="0"
            placeholder="5.0"
            {...register('tariffRate', { valueAsNumber: true })}
            disabled={isLoading}
          />
          {material && (
            <p className="text-xs text-muted-foreground">
              Al actualizar, se creara un nuevo registro en el historial de tarifas
            </p>
          )}
        </div>
      </div>

      {selectedType === 'PRODUCT' && (
        <ProductFields
          register={register}
          errors={errors as any}
          setValue={setValue}
          watch={watch}
          isLoading={isLoading}
        />
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Guardando...
            </>
          ) : material ? (
            'Actualizar'
          ) : (
            'Crear'
          )}
        </Button>
      </div>
    </form>
  );
}

export default MaterialForm;
